import { useEffect, useMemo, useRef, useState } from "react";
import type { Interval } from "@models/ISchema";
import type { AnnualScheduleGroup } from "../../../../adapters";
import type { AnnualScheduleCellSelection } from "../../../../types";

interface DrawerDetails {
  scopeKey: string;
  selection: AnnualScheduleCellSelection;
  leagueGroup: AnnualScheduleGroup;
  week: Interval;
}

interface DrawerLifecycleOptions {
  isOpen: boolean;
  scopeKey: string;
  selection?: AnnualScheduleCellSelection | null;
  leagueGroup?: AnnualScheduleGroup;
  week?: Interval;
  onClose: () => void;
}

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), [tabindex="0"]';
const AVOCADO_OPEN_GUARD_MS = 40;

/** Keeps keyboard focus inside the drawer when Tab reaches either end of its controls. */
const keepFocusInDrawer = (event: KeyboardEvent, drawer: HTMLElement) => {
  const focusableElements = Array.from(drawer.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
  const first = focusableElements[0];
  const last = focusableElements[focusableElements.length - 1];

  if (!first) {
    event.preventDefault();
    return;
  }

  if (event.shiftKey && (document.activeElement === first || document.activeElement === drawer)) {
    event.preventDefault();
    last.focus();
  } else if (
    !event.shiftKey &&
    (document.activeElement === last || document.activeElement === drawer)
  ) {
    event.preventDefault();
    first.focus();
  }
};

/**
 * Coordinates Avocado's delayed opening and closing transitions with the selected cell.
 *
 * @param options - Drawer state, complete cell details, and the close callback.
 * @returns The retained details for the exit transition, the guarded open state, and
 * callbacks used by Avocado's title and completion events.
 *
 * @remarks Avocado opens a newly mounted panel after a short timer. This hook keeps
 * its `isOpen` prop true long enough for rapid deselection to close cleanly. It also
 * traps focus while open and restores focus to the triggering control after closing.
 */
export const useAnnualDrawerLifecycle = ({
  isOpen,
  scopeKey,
  selection,
  leagueGroup,
  week,
  onClose,
}: DrawerLifecycleOptions) => {
  const [headingElement, setHeadingElement] = useState<HTMLDivElement | null>(null);
  const [isSuiteDrawerOpen, setSuiteDrawerOpen] = useState(false);
  const lastDetailsRef = useRef<DrawerDetails | null>(null);
  const triggerElementRef = useRef<HTMLElement | null>(null);
  const openingStartedAtRef = useRef<number | null>(null);
  const currentDetails = useMemo(
    () =>
      isOpen && selection && leagueGroup && week
        ? { scopeKey, selection, leagueGroup, week }
        : null,
    [isOpen, scopeKey, selection, leagueGroup, week],
  );
  const isDrawerOpen = !!currentDetails;

  useEffect(() => {
    if (isDrawerOpen) {
      if (!isSuiteDrawerOpen) {
        openingStartedAtRef.current = Date.now();
        setSuiteDrawerOpen(true);
      }
      return;
    }

    if (!isSuiteDrawerOpen) return;

    const elapsed = Date.now() - (openingStartedAtRef.current ?? 0);
    const timeout = window.setTimeout(
      () => {
        setSuiteDrawerOpen(false);
        openingStartedAtRef.current = null;
      },
      Math.max(0, AVOCADO_OPEN_GUARD_MS - elapsed),
    );

    return () => window.clearTimeout(timeout);
  }, [isDrawerOpen, isSuiteDrawerOpen]);

  // Retain the previous cell until Avocado finishes its closing transition.
  useEffect(() => {
    if (currentDetails) lastDetailsRef.current = currentDetails;
  }, [currentDetails]);

  const details =
    currentDetails ??
    (lastDetailsRef.current?.scopeKey === scopeKey ? lastDetailsRef.current : null);

  // Avocado mounts the panel after isOpen changes; its title ref signals when it exists.
  useEffect(() => {
    if (!isDrawerOpen || !details || !headingElement) return;

    const drawerElement = headingElement.closest<HTMLElement>("section");
    if (!drawerElement) return;

    if (!triggerElementRef.current) {
      const activeElement = document.activeElement;
      if (
        activeElement instanceof HTMLElement &&
        activeElement !== document.body &&
        !drawerElement.contains(activeElement)
      ) {
        triggerElementRef.current = activeElement;
      }
    }
    drawerElement.setAttribute("role", "dialog");
    drawerElement.setAttribute("aria-modal", "true");
    drawerElement.setAttribute(
      "aria-label",
      `${details.week.name} · ${details.leagueGroup.course?.name ?? details.leagueGroup.course?.code}`,
    );
    drawerElement.tabIndex = -1;
    drawerElement.focus({ preventScroll: true });

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        onClose();
        return;
      }

      if (event.key === "Tab") keepFocusInDrawer(event, drawerElement);
    };

    drawerElement.addEventListener("keydown", onKeyDown);
    return () => {
      drawerElement.removeEventListener("keydown", onKeyDown);
    };
  }, [
    isDrawerOpen,
    headingElement,
    details?.week.name,
    details?.leagueGroup.course?.name,
    details?.leagueGroup.course?.code,
    onClose,
  ]);

  /** Restores focus after Avocado reports that its closing transition has finished. */
  const handleDrawerClosed = () => {
    if (isDrawerOpen) onClose();

    const triggerElement = triggerElementRef.current;
    triggerElementRef.current = null;
    if (triggerElement?.isConnected) triggerElement.focus();
  };

  // Navigation can unmount the drawer before Avocado reports transition completion.
  useEffect(
    () => () => {
      const triggerElement = triggerElementRef.current;
      if (triggerElement?.isConnected) triggerElement.focus();
    },
    [],
  );

  return { details, isDrawerOpen, isSuiteDrawerOpen, setHeadingElement, handleDrawerClosed };
};
